import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "flowbite-react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../Reducers/store";
import {
  cancelMeetingRequest,
  confirmMeeting,
  getMeetingRequests,
  rsvpMeeting,
  type MeetingRequestDetails,
} from "../../Api/ScheduleApi";
import { formatInstantRange } from "../../Utils/timeZone";

function formatMeetingEt(startIso: string, endIso: string, timeZoneId?: string | null): string {
  return formatInstantRange(startIso, endIso, timeZoneId);
}

function isPendingInvite(req: MeetingRequestDetails, userId: number | undefined): boolean {
  if (!userId) return false;
  const me = req.participants?.find((p) => p.userId === userId);
  const isInvitee =
    userId === req.recipientUserId || (!!me && me.role !== "organizer");
  if (!isInvitee || userId === req.requesterUserId) return false;
  if (req.status === 0) return !me || me.status === "pending";
  if (req.meetingId && req.myRsvpStatus === "pending") return true;
  return false;
}

/**
 * Dashboard panel: pending meeting invites with Accept / Decline in place.
 * Renders nothing when there are no pending invites.
 */
export default function PendingMeetingRequests() {
  const { userModel } = useAppSelector((s) => s.user);
  const userId = userModel?.id;
  const [requests, setRequests] = useState<MeetingRequestDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!userId) {
      setRequests([]);
      setLoading(false);
      return;
    }
    try {
      const data = await getMeetingRequests();
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const pending = useMemo(
    () => requests.filter((r) => isPendingInvite(r, userId)),
    [requests, userId]
  );

  const handleAccept = async (req: MeetingRequestDetails) => {
    setBusyId(req.id);
    try {
      if (req.status === 0) {
        await confirmMeeting(req.id);
      } else if (req.meetingId) {
        await rsvpMeeting(req.meetingId, true);
      }
      toast.success("Meeting accepted.");
      await load();
    } catch {
      toast.error("Unable to accept this meeting.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDecline = async (req: MeetingRequestDetails) => {
    if (!window.confirm("Decline this meeting invite?")) return;
    setBusyId(req.id);
    try {
      if (req.meetingId && req.status !== 0) {
        await rsvpMeeting(req.meetingId, false);
      } else {
        await cancelMeetingRequest(req.id);
      }
      toast.success("Meeting declined.");
      await load();
    } catch {
      toast.error("Unable to decline this meeting.");
    } finally {
      setBusyId(null);
    }
  };

  if (!userId || loading || pending.length === 0) return null;

  return (
    <div className="mx-4 mb-6 rounded-lg border-2 border-amber-400 bg-amber-50 p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold text-amber-950">
          Meeting invites awaiting your response
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-amber-800">
            {pending.length} pending
          </span>
          <Link
            to="/schedule"
            className="text-sm font-semibold text-green-800 underline"
          >
            View full schedule
          </Link>
        </div>
      </div>
      <ul className="space-y-3">
        {pending.map((req) => (
          <li
            key={req.id}
            className="rounded-md border border-amber-200 bg-white p-3 shadow-sm"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900">
                  From {req.requesterName}
                  {req.requesterMemberName
                    ? ` · ${req.requesterMemberName}`
                    : ""}
                </p>
                <p className="mt-0.5 text-sm text-gray-700">
                  {formatMeetingEt(req.startUtc, req.endUtc, req.timeZoneId)}
                </p>
                {req.message ? (
                  <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                    {req.message}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  color="success"
                  size="sm"
                  disabled={busyId === req.id}
                  onClick={() => handleAccept(req)}
                >
                  {busyId === req.id ? "…" : "Accept"}
                </Button>
                <Button
                  color="failure"
                  size="sm"
                  outline
                  disabled={busyId === req.id}
                  onClick={() => handleDecline(req)}
                >
                  Decline
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
